<template>
  <div class="width-responsiveness">
    <div class="batch-sample-list">
      <batch-sample-filter
        v-if="!isEditingDisabled"
        v-show="!isSamplesLoading && (samples.length > 0 || hasActiveSearch)"
        ref="batchSampleFilterRef"
        :text-button-remove="textButtonRemove"
        :disabled-button-remove="isRemoveButtonDisabled"
        :batch="batch"
        @request-fetch-batch="handleRequestFetchBatch"
        @samples-removed="clearSelection"
        @search-active="hasActiveSearch = $event"
      />

      <div
        v-if="isSamplesLoading || isLoading"
        class="batch-sample-list-loading"
      >
        <spinner />
      </div>

      <template v-else>
        <div
          v-if="samples.length === 0"
          class="batch-sample-list-empty"
        >
          <template v-if="hasActiveSearch">
            <p>Nenhum resultado encontrado para a busca realizada.</p>

            <md-button
              class="btn btn-nonenary"
              @click="clearSearch"
            >
              Listar todos
            </md-button>
          </template>

          <p v-else>Inclua uma amostra neste lote para definir suas características.</p>
        </div>

        <div v-else>
          <div class="print-tag-batches__card-container">
            <div class="collapsed">
              <div class="md-list">
                <div class="print-tag-batch-details-list_container">
                  <table class="apoio-datatable relative-head">
                    <colgroup>
                      <col class="column-checkbox" />

                      <col class="column-lis-number" />

                      <col class="column-patient" />

                      <col class="column-material" />

                      <col class="column-os-alvaro" />

                      <col class="column-document" />

                      <col class="column-expander" />
                    </colgroup>

                    <thead>
                      <tr>
                        <th class="column-checkbox">
                          <div class="custom-checkbox">
                            <input
                              id="checkOrderSelectAllElement"
                              v-model="selectAll"
                              type="checkbox"
                              @change="toggleSelectAll"
                            />

                            <label for="checkOrderSelectAllElement" />
                          </div>
                        </th>

                        <th @click.prevent="sortColumn('os_lis')">
                          <span
                            title="OS LIS"
                            class="sort-column"
                          >
                            <template v-if="!$toggle.feature.covax">OS LIS</template>

                            <template v-else>Requisition Number</template>
                          </span>

                          <sort-column
                            id="os_lis"
                            @click.prevent="sortColumn('os_lis')"
                          />
                        </th>

                        <th
                          title="PACIENTE (ID LIS)"
                          class="sort-column"
                          @click.prevent="sortColumn('patient_name')"
                        >
                          PACIENTE (ID LIS)

                          <sort-column id="patient_name" />
                        </th>

                        <th @click.prevent="sortColumn('material_description')">
                          <span
                            title="MATERIAL (ACOND)"
                            class="sort-column"
                          >
                            MATERIAL (ACOND)
                          </span>

                          <sort-column
                            id="material_description"
                            @click.prevent="sortColumn('os_lis')"
                          />
                        </th>

                        <th
                          title="OS ÁLVARO"
                          class="sort-column"
                          @click.prevent="sortColumn('number_alvaro_order')"
                        >
                          OS ÁLVARO
                          <sort-column id="number_alvaro_order" />
                        </th>

                        <th
                          title="PENDÊNCIA"
                          class="sort-column"
                          @click.prevent="sortColumn('pending_doc')"
                        >
                          PENDÊNCIA

                          <sort-column id="pending_doc" />
                        </th>

                        <th class="column-expander" />
                      </tr>
                    </thead>
                  </table>

                  <table class="apoio-datatable">
                    <colgroup>
                      <col class="column-checkbox" />

                      <col class="column-lis-number" />

                      <col class="column-patient" />

                      <col class="column-material" />

                      <col class="column-os-alvaro" />

                      <col class="column-document center" />

                      <col class="column-expander" />
                    </colgroup>

                    <tbody
                      v-for="(sample, index) in samples"
                      :key="index"
                      :md-item="sample"
                    >
                      <tr
                        :class="{ expanded: openSamplesDetails.includes(index) }"
                        class="apoio-datatable__line"
                      >
                        <td class="column-checkbox">
                          <div class="custom-checkbox">
                            <input
                              v-model="selectedSamples"
                              type="checkbox"
                              :value="sample.sample_id || sample.bar_code"
                              :disabled="isSamplePaternity(sample)"
                            />

                            <label :for="'checkbox-' + index" />
                          </div>
                        </td>

                        <td @click.prevent="openSampleDetails(index, sample)">
                          <span :title="sample.os_lis">{{ sample.os_lis }}</span>
                        </td>

                        <td @click.prevent="openSampleDetails(index, sample)">
                          <span
                            :title="
                              concatNameCode(sample.patient_name, sample.patient_id_lis)
                            "
                            class="patient-name-span"
                          >
                            {{
                              concatNameCode(sample.patient_name, sample.patient_id_lis)
                            }}
                          </span>
                        </td>

                        <td @click.prevent="openSampleDetails(index, sample)">
                          <span
                            :title="
                              concatNameCode(
                                sample.material_description,
                                sample.material_packaging
                              )
                            "
                          >
                            {{
                              concatNameCode(
                                sample.material_description,
                                sample.material_packaging
                              )
                            }}
                          </span>
                        </td>

                        <td @click.prevent="openSampleDetails(index, sample)">
                          <span :title="sample.number_alvaro_order">
                            {{ sample.number_alvaro_order }}
                          </span>
                        </td>

                        <td
                          class="column-document"
                          @click.prevent="openSampleDetails(index, sample)"
                        >
                          <div class="document-icon-content">
                            <svg-loader
                              v-if="sample.printed_status"
                              src="/static/images/icon-impressed.svg"
                            />

                            <svg-loader
                              v-else
                              src="/static/images/icon-not-printed.svg"
                            />

                            <document-icon
                              v-if="sample.docRequired"
                              :is-document-delivered="sample.docDelivered"
                            />
                          </div>
                        </td>

                        <td @click.prevent="openSampleDetails(index, sample)">
                          <svg-loader
                            src="/static/images/arrow-up-primary-color.svg"
                            class="arrow-icon"
                            :class="{
                              'arrow-icon--rotated': openSamplesDetails.includes(index),
                            }"
                          />
                        </td>
                      </tr>

                      <tr class="row-details">
                        <td
                          colspan="7"
                          class="row-details__container"
                        >
                          <batch-sample-details
                            :ref="`sampleDetails-${index}`"
                            :sample="sample"
                            @refresh-samples="getSamplesFromBatch()"
                            @toggle-visibility="handleToggleVisibility($event, index)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'

import { concatNameAndCode } from '@/utils/string-utils'
import { setSortation } from '@/utils/sort-columns'

import { BATCH_STATE_PRINTED } from '@/enums'

const PATERNITY_EXAM_CODES = ['PATMATER', 'PAT', 'PATDUO', 'PATRECONS']

import strings from '@/strings'

import Spinner from '@/components/common/Spinner'
import SortColumn from '@/components/common/SortColumn'
import loadingInfiniteMessage from '@/components/common/LoadingInfiniteMessage'
import DocumentIcon from '@/components/Documents/Laboratory/Flag'
import DocumentFilter from '@/components/Documents/Laboratory/Filter'
import EmptyCart from '@/components/common/EmptyCart'
import Alert from '@/components/common/Alert'
import PrintServerAlert from '@/components/Dashboard/PrintServerAlert'

import BatchSampleDetails from './BatchSampleDetails'
import BatchSampleFilter from './BatchSampleFilter'

export default {
  name: 'BatchSampleList',
  components: {
    InfiniteLoading,
    Spinner,
    BatchSampleDetails,
    SortColumn,
    loadingInfiniteMessage,
    DocumentIcon,
    DocumentFilter,
    EmptyCart,
    Alert,
    PrintServerAlert,
    BatchSampleFilter,
  },
  props: {
    printTagIndex: Number,
    isEditingDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    batch: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      textButtonRemove: 'REMOVER DO LOTE',
      BATCH_STATE_PRINTED: BATCH_STATE_PRINTED,
      isBatchModalLoading: true,
      columnSortStatusSamples: {
        os_lis: 'asc',
        patient_name: 'asc',
        material_description: 'asc',
        number_alvaro_order: 'asc',
      },
      totalSampleExamsWithDocPending: 0,
      alertSamples: strings.alerts.alertSamples,
      selectedSamples: [],
      selectAll: false,
      openSamplesDetails: [],
      hasActiveSearch: false,
    }
  },
  computed: {
    ...mapGetters([
      'samplePrintingStatus',
      'profile',
      'toggle',
      'batchSamplesSorted',
      'isLoadingBatchSamples',
    ]),
    samples() {
      return this.batchSamplesSorted.samples || []
    },
    isSamplesLoading() {
      return this.isLoadingBatchSamples || this.isLoading
    },
    isRemoveButtonDisabled() {
      return this.selectedSamples.length === 0
    },
  },
  watch: {
    selectedSamples(newValue) {
      this.setBatchSelectedSamples(
        newValue.map(id =>
          this.samples.find(sample => sample.sample_id === id || sample.bar_code === id)
        )
      )
      this.updateRemoveButtonText()
    },
    samples() {
      this.openSamplesDetails.forEach(index => {
        this.$refs[`sampleDetails-${index}`]?.[0]?.toggleVisibility?.()
      })

      this.openSamplesDetails = []
    },
  },
  async mounted() {
    await this.loadBatchDetailsData()

    if (this.$route.query.orderPrintBy) {
      this.$nextTick(() => {
        setTimeout(() => this.sortColumn(this.$route.query.orderPrintBy))
      })
    }
  },
  beforeDestroy() {
    this.setBatchSelectedSamples([])
    this.clearBatchDetailsSamples()
  },
  methods: {
    ...mapActions([
      'updateBatchDetailsOpened',
      'batchSetSortToSamples',
      'setBatchSelectedSamples',
      'fetchBatchSamples',
      'clearBatchDetailsSamples',
    ]),
    updateRemoveButtonText() {
      const count = this.selectedSamples.length
      this.textButtonRemove = count > 0 ? `REMOVER DO LOTE (${count})` : 'REMOVER DO LOTE'
    },
    isSamplePaternity(sample) {
      if (this.batch.paternity) return true
      if (sample.paternity_name) return true
      return sample.exams?.some(exam =>
        PATERNITY_EXAM_CODES.includes(exam.code?.toUpperCase())
      )
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedSamples = this.samples
          .filter(s => !this.isSamplePaternity(s))
          .map(s => s.sample_id || s.bar_code)
      } else {
        this.selectedSamples = []
      }
    },
    async loadBatchDetailsData() {
      const batchId = this.$route.params.batchid

      if (batchId) {
        await this.fetchBatchSamples({
          batchId,
        })
      }
    },
    sortColumn(column, columnChild) {
      this.columnSortStatusSamples[column] = setSortation(
        column,
        columnChild,
        this.columnSortStatusSamples,
        this.batchSetSortToSamples
      )

      this.pushEventToAnalytics('Ordenação da coluna ' + column)
    },
    openSampleDetails(index) {
      this.$refs[`sampleDetails-${index}`]?.[0]?.toggleVisibility?.()
    },
    concatNameCode(name, code) {
      return concatNameAndCode(name, code)
    },
    pushEventToAnalytics(action) {
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Detalhes do Lote',
        action: action,
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName,
      })
    },
    handleToggleVisibility(isVisible, index) {
      if (isVisible) {
        this.openSamplesDetails = [...new Set([...this.openSamplesDetails, index])]
      } else {
        this.openSamplesDetails = this.openSamplesDetails.filter(item => item !== index)
      }
    },
    handleRequestFetchBatch() {
      this.$emit('request-fetch-batch')
    },
    clearSelection() {
      this.selectedSamples = []
      this.selectAll = false
      this.setBatchSelectedSamples([])
    },
    clearSearch() {
      this.$refs.batchSampleFilterRef?.clearSearch()
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.width-responsiveness {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  min-width: 700px !important;
}

.batch-sample-list {
  .batch-sample-list-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    color: #666666;
    height: 256px;
    flex-direction: column;
    gap: 14px;
  }

  .batch-sample-list-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 256px;
  }

  .collapsed {
    z-index: 0 !important;
  }

  &__card-container {
    align-items: flex-start;
    display: flex;
    height: 100%;
    flex-grow: 1;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-left: 0;
    padding: 0;

    .collapsed {
      width: 100%;
      display: flex;
    }
  }

  .md-list {
    width: 100%;
    padding: 0;
  }
}

.apoio-datatable {
  width: 100% !important;
  margin-bottom: 0;

  &:after {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .column {
    &-checkbox {
      padding-left: 0;
      text-align: left !important;
      width: 1.25rem !important;
    }

    &-expander {
      padding-right: 0;
      text-align: right !important;
      width: 1.375rem !important;
    }

    &-document {
      .document-icon-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        width: fit-content;
      }

      .md-icon {
        width: 28px;
        height: 28px;
        min-width: auto;
        min-height: auto;
      }
    }
  }

  &__line {
    border-bottom: none !important;
  }

  .row-details {
    &__container {
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  .arrow-icon {
    color: $theme-primary-color;
  }
}

.icon-arrow-dropdown {
  &--active {
    transform: rotate(180deg);
  }
}

.arrow-icon {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.arrow-icon--rotated {
  transform: rotate(0deg);
}

.card-container {
  width: 22%;
  .md-card {
    width: 100%;
  }
}

.print-tag-batch-details-list_container {
  vertical-align: center;

  .apoio-datatable {
    margin-top: 0;
  }
}

.custom-checkbox {
  position: relative;
  z-index: 1;

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid $theme-primary-color;
    border-radius: 4px;
    background-color: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;

    &:checked {
      background-color: $theme-primary-color;
    }

    &:disabled {
      border-color: #ccc;
      background-color: #f0f0f0;
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  label {
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    pointer-events: none;
  }
}
</style>
